/**
 * DataRoomClaims (Phase 116.7-04)
 *
 * Investor-only surface for data-room-sourced verifiable claims. Renders claims
 * grouped by source document, each with a verification-status badge and a
 * "Data Room" origin badge (to distinguish from pitch-deck evidence). On
 * verified/contradicted rows the linked verification source ("why") is shown.
 *
 * Reuses the existing theme palette + badge patterns from DimensionCard /
 * score-display — no new colors. Follows CLAUDE.md layout-containment rules:
 * numberOfLines on row text, overflow-hidden rounded cards, flex-wrap badge
 * rows, stable `claim-${id}` keys, isWeb sizing.
 */

import React, { useMemo } from 'react';
import { View, Text, ActivityIndicator, Pressable } from 'react-native';
import { FileText, ShieldCheck, ExternalLink, AlertTriangle } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { openExternalUrl } from '@/lib/linking';
import {
  useDataRoomClaims,
  type DataRoomClaim,
} from '@/lib/supabase/claim-queries';
import {
  verificationStatusToBadge,
  claimBadgeToneColor,
  claimBadgeToneBgColor,
} from '@/lib/scoring/score-display';

interface DataRoomClaimsProps {
  startupId: string;
  /** Gate: this surface is investor-only. */
  role: 'startup' | 'investor';
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Turn a snake_case claim_type into a Title Case label. */
function humanizeClaimType(type: string): string {
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface ClaimGroup {
  documentId: string | null;
  label: string;
  claims: DataRoomClaim[];
}

/** Group claims by source document, preserving the query's document order. */
function groupByDocument(claims: DataRoomClaim[]): ClaimGroup[] {
  const order: (string | null)[] = [];
  const map = new Map<string | null, DataRoomClaim[]>();
  for (const claim of claims) {
    const key = claim.sourceDocumentId;
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(claim);
  }
  return order.map((key, i) => ({
    documentId: key,
    label: key ? `Document ${i + 1}` : 'Unattributed',
    claims: map.get(key)!,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function StatusBadge({
  status,
  colors,
}: {
  status: DataRoomClaim['verificationStatus'];
  colors: ReturnType<typeof useThemeColors>;
}) {
  const { label, tone } = verificationStatusToBadge(status);
  const fg = claimBadgeToneColor(tone, colors);
  const bg = claimBadgeToneBgColor(tone, colors);
  return (
    <View className="px-2 py-0.5 rounded-md" style={{ backgroundColor: bg }}>
      <Text className="text-[10px] font-bold" style={{ color: fg }} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

function OriginBadge({ colors }: { colors: ReturnType<typeof useThemeColors> }) {
  return (
    <View
      className="flex-row items-center gap-1 px-2 py-0.5 rounded-md"
      style={{ backgroundColor: `${colors.info}18` }}
    >
      <ShieldCheck size={10} color={colors.info} />
      <Text className="text-[10px] font-semibold" style={{ color: colors.info }} numberOfLines={1}>
        Data Room
      </Text>
    </View>
  );
}

function ClaimRow({
  claim,
  colors,
}: {
  claim: DataRoomClaim;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const showWhy =
    (claim.verificationStatus === 'verified' || claim.verificationStatus === 'contradicted') &&
    (!!claim.sourceSummary || !!claim.sourceUrl);

  const openSource = () => {
    openExternalUrl(claim.sourceUrl, 'DataRoomClaims.openSource');
  };

  return (
    <View
      className="rounded-xl overflow-hidden p-3"
      style={{ backgroundColor: colors.surface.secondary }}
    >
      {/* Entity */}
      <Text
        className="text-xs font-semibold"
        style={{ color: colors.text.primary }}
        numberOfLines={1}
      >
        {claim.entity}
      </Text>

      {/* Assertion */}
      <Text
        className="text-xs leading-4 mt-1"
        style={{ color: colors.text.secondary }}
        numberOfLines={3}
      >
        {claim.assertion}
      </Text>

      {/* Badge row: status + claim type + origin */}
      <View className="flex-row items-center gap-1.5 mt-2 flex-wrap">
        <StatusBadge status={claim.verificationStatus} colors={colors} />
        <View
          className="px-2 py-0.5 rounded-md"
          style={{ backgroundColor: colors.surface.tertiary }}
        >
          <Text
            className="text-[10px] font-semibold"
            style={{ color: colors.text.tertiary }}
            numberOfLines={1}
          >
            {humanizeClaimType(claim.claimType)}
          </Text>
        </View>
        <OriginBadge colors={colors} />
      </View>

      {/* "Why" — verification source on verified/contradicted rows */}
      {showWhy ? (
        <View
          className="rounded-lg px-2.5 py-2 mt-2"
          style={{
            backgroundColor: colors.surface.tertiary,
            borderLeftWidth: 2,
            borderLeftColor: colors.info,
          }}
        >
          {claim.sourceSummary ? (
            <Text
              className="text-[11px] leading-4"
              style={{ color: colors.text.secondary }}
              numberOfLines={3}
            >
              {claim.sourceSummary}
            </Text>
          ) : null}
          {claim.sourceUrl ? (
            <Pressable
              onPress={openSource}
              className="flex-row items-center gap-1 mt-1"
              accessibilityRole="link"
              accessibilityLabel="Open verification source"
            >
              <ExternalLink size={11} color={colors.info} />
              <Text
                className="text-[11px] font-medium flex-1"
                style={{ color: colors.info }}
                numberOfLines={1}
              >
                {claim.sourceUrl}
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function DataRoomClaims({ startupId, role }: DataRoomClaimsProps) {
  const colors = useThemeColors();
  const { data: claims, isLoading, error } = useDataRoomClaims(startupId);

  const groups = useMemo(() => groupByDocument(claims ?? []), [claims]);

  // Investor-only surface (mirror the VerificationTable gate in DimensionCard).
  if (role !== 'investor') return null;

  if (isLoading) {
    return (
      <View className="items-center py-6">
        <ActivityIndicator size="small" color={colors.primary[600]} />
        <Text
          className="text-xs mt-2"
          style={{ color: colors.text.tertiary }}
          numberOfLines={1}
        >
          Loading data-room claims...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View
        className="rounded-xl overflow-hidden p-3 flex-row items-start gap-2"
        style={{ backgroundColor: colors.red.light }}
      >
        <AlertTriangle size={14} color={colors.error} style={{ marginTop: 1 }} />
        <Text
          className="text-xs leading-4 flex-1"
          style={{ color: colors.error }}
          numberOfLines={3}
        >
          Couldn&apos;t load data-room claims. Pull to refresh or try again later.
        </Text>
      </View>
    );
  }

  if (!claims || claims.length === 0) {
    return (
      <View
        className="rounded-xl overflow-hidden p-4 items-center"
        style={{ backgroundColor: colors.surface.secondary }}
      >
        <FileText size={20} color={colors.text.tertiary} />
        <Text
          className="text-sm font-medium mt-2"
          style={{ color: colors.text.secondary }}
          numberOfLines={1}
        >
          No data-room claims yet
        </Text>
        <Text
          className="text-xs mt-1 text-center"
          style={{ color: colors.text.tertiary }}
          numberOfLines={2}
        >
          Claims appear here once the founder&apos;s data room is processed and verified.
        </Text>
      </View>
    );
  }

  return (
    <View style={{ gap: isWeb ? 16 : 14 }}>
      {groups.map((group, gi) => (
        <View key={`claim-group-${group.documentId ?? 'none'}-${gi}`} style={{ gap: 8 }}>
          {/* Document header */}
          <View className="flex-row items-center gap-1.5">
            <FileText size={13} color={colors.text.tertiary} />
            <Text
              style={{
                color: colors.text.tertiary,
                fontSize: 9,
                fontWeight: '700',
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                flex: 1,
              }}
              numberOfLines={1}
            >
              {group.label} · {group.claims.length} claim{group.claims.length === 1 ? '' : 's'}
            </Text>
          </View>

          {/* Claim rows */}
          {group.claims.map((claim) => (
            <ClaimRow key={`claim-${claim.id}`} claim={claim} colors={colors} />
          ))}
        </View>
      ))}
    </View>
  );
}

export default DataRoomClaims;
